import { useEffect, useReducer } from "react"
import {
  defaultIndustry,
  defaultCompanySize,
  defaultRecruiterType,
  recruiterTypes,
} from "./defaults"
import { routes } from "@/helpers"

const initialState = {
  initialBenchmarkSettings: {},
  userBenchmarkSettings: {},
  defaultBenchmarkSettings: {
    recruiterType: defaultRecruiterType,
    industryId: defaultIndustry.id,
    companySizeId: defaultCompanySize.id,
  },
  isSetByUser: false,
  isAdminSection: false,
  benchmarkSettings: {},
  benchmarkSettingsChanged: false,

  companySizes: [defaultCompanySize],
  industries: [defaultIndustry],
  loading: true,
  readyToFetchCompanySizes: false,
}

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_USER_BENCHMARK_SETTINGS":
      return { ...state, loading: true, userBenchmarkSettings: action.value }

    case "SET_INITIAL_BENCHMARK_SETTINGS":
      const {
        initialIndustryId,
        initialRecruiterType,
        initialCompanySizeId,
      } = action.value

      return {
        ...state,
        loading: true,
        initialBenchmarkSettings: {
          recruiterTypeId: initialRecruiterType,
          industryId: initialIndustryId,
          companySizeId: initialCompanySizeId,
        },
      }

    case "CALCULATE_BENCHMARK_SETTINGS":
      const {
        initialBenchmarkSettings,
        userBenchmarkSettings: {
          recruiterType: userRecruiterType,
          industry: userIndustry,
          companySize: userCompanySize,
        },
        isSetByUser,
        isAdminSection,
        benchmarkSettings,
        industries,
        companySizes,
      } = state

      const findIndustry = industryId =>
        industryId && industries.find(({ id }) => id === industryId)

      const findCompanySize = companySizeId =>
        companySizeId && companySizes.find(({ id }) => id === companySizeId)

      const recruiterType = isSetByUser
        ? benchmarkSettings.recruiterType
        : recruiterTypes[initialBenchmarkSettings.recruiterTypeId] ||
          userRecruiterType ||
          defaultRecruiterType

      const industry = isSetByUser
        ? benchmarkSettings.industry
        : findIndustry(initialBenchmarkSettings.industryId) ||
          userIndustry ||
          defaultIndustry

      const companySizeId = isSetByUser
        ? benchmarkSettings.companySize.id
        : initialBenchmarkSettings.companySizeId || userCompanySize?.id

      const companySize = findCompanySize(companySizeId)

      const calculateCompanySize = () => {
        if (!companySize) return defaultCompanySize
        if (isAdminSection) return companySize

        return companySize.enoughData ? companySize : defaultCompanySize
      }

      const calculateIndustry = () => {
        if (isAdminSection) return industry

        return recruiterType === defaultRecruiterType
          ? industry
          : defaultIndustry
      }

      return {
        ...state,
        readyToFetchCompanySizes: true,
        benchmarkSettings: {
          recruiterType,
          industry: calculateIndustry(),
          companySize: calculateCompanySize(),
        },
      }

    case "SET_RECRUITER_TYPE":
      return {
        ...state,
        benchmarkSettings: {
          ...state.benchmarkSettings,
          recruiterType: action.value,
          industry: defaultIndustry,
          companySize: defaultCompanySize,
        },
        isSetByUser: true,
      }

    case "SET_INDUSTRY":
      return {
        ...state,
        benchmarkSettings: {
          ...state.benchmarkSettings,
          industry: action.value,
          companySize: defaultCompanySize,
        },
        isSetByUser: true,
      }

    case "SET_COMPANY_SIZE":
      return {
        ...state,
        benchmarkSettings: {
          ...state.benchmarkSettings,
          companySize: action.value,
        },
        isSetByUser: true,
      }

    case "SET_INDUSTRIES":
      return { ...state, industries: [defaultIndustry, ...action.value] }

    case "SET_COMPANY_SIZES":
      return { ...state, companySizes: [defaultCompanySize, ...action.value] }

    case "SET_READY":
      return { ...state, loading: false }

    case "SET_BENCHMARK_SETTINGS":
      return {
        ...state,
        benchmarkSettings: {
          ...state.benchmarkSettings,
          ...action.value,
        },
        isSetByUser: true,
        benchmarkSettingsChanged: true,
      }

    case "RESET_FLAG_BENCHMARK_SETTINGS_CHANGED":
      return {
        ...state,
        benchmarkSettingsChanged: false,
      }

    case "SET_ADMIN_SECTION":
      return {
        ...state,
        isAdminSection: RegExp(routes.administration).test(
          window.location.pathname,
        ),
      }

    default:
      throw new Error("Action not implemented")
  }
}

const parsedBenchmarkSettings = benchmarkSettings => {
  if (benchmarkSettings?.recruiterType === "none") {
    return {
      ...benchmarkSettings,
      recruiterType: defaultRecruiterType,
    }
  }

  return benchmarkSettings
}

const useBenchmarkSettingsState = ({
  industries,
  benchmarkSettings,
  benchmarkSettingsRefetch,
  fetchCompanySizes,
  onSetBenchmarkSettings,
  initialRecruiterType,
  initialIndustryId,
  initialCompanySizeId,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  // Refetch benchmark settings after the mutation that save them
  useEffect(() => {
    if (state.benchmarkSettingsChanged) {
      benchmarkSettingsRefetch()
      dispatch({ type: "RESET_FLAG_BENCHMARK_SETTINGS_CHANGED" })
    }
  }, [state.benchmarkSettingsChanged, benchmarkSettingsRefetch])

  // Consolidate benchmarkSettings when the component is mounted
  useEffect(() => {
    dispatch({ type: "SET_ADMIN_SECTION" })
    dispatch({
      type: "SET_USER_BENCHMARK_SETTINGS",
      value: parsedBenchmarkSettings(benchmarkSettings),
    })
    dispatch({ type: "SET_INDUSTRIES", value: industries })

    dispatch({
      type: "SET_INITIAL_BENCHMARK_SETTINGS",
      value: {
        initialRecruiterType,
        initialIndustryId,
        initialCompanySizeId,
      },
    })

    dispatch({ type: "CALCULATE_BENCHMARK_SETTINGS" })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch company sizes when industry changes
  useEffect(() => {
    if (!state.readyToFetchCompanySizes) return
    ;(async () => {
      const companySizes = await fetchCompanySizes(state.benchmarkSettings)

      dispatch({ type: "SET_COMPANY_SIZES", value: companySizes })
      dispatch({ type: "CALCULATE_BENCHMARK_SETTINGS" })

      dispatch({ type: "SET_READY" })
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.benchmarkSettings.industry, state.benchmarkSettings.recruiterType])

  const setRecruiterType = recruiterTypes =>
    dispatch({ type: "SET_RECRUITER_TYPE", value: recruiterTypes })

  const setIndustry = industry =>
    dispatch({ type: "SET_INDUSTRY", value: industry })

  const setCompanySize = companySize =>
    dispatch({ type: "SET_COMPANY_SIZE", value: companySize })

  const setBenchmarkSettings = async benchmarkSettings => {
    const bechmarkSettingsMutation = await onSetBenchmarkSettings(
      benchmarkSettings,
    )

    if (bechmarkSettingsMutation) {
      dispatch({ type: "SET_BENCHMARK_SETTINGS", value: benchmarkSettings })
    }
  }

  return {
    state,
    setRecruiterType,
    setIndustry,
    setCompanySize,
    setBenchmarkSettings,
  }
}

export default useBenchmarkSettingsState
